import { ArrowLeft, ListTree } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { DocumentHeading } from '../../lib/document-reader'
import { cn } from '../../lib/utils'

interface DocumentTableOfContentsProps {
  headings: DocumentHeading[]
  className?: string
  headingSelector?: string
  useHashLinks?: boolean
  collapsible?: boolean
  backTo?: string
  backLabel?: string
}

function scrollToHeading(index: number, selector: string) {
  const nodes = Array.from(document.querySelectorAll<HTMLElement>(selector))
  const target = nodes[index]
  if (!target) return

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' })
}

export function DocumentTableOfContents({
  headings,
  className,
  headingSelector = ".aw-doc-reader-surface .bn-block-content[data-content-type='heading']",
  useHashLinks = false,
  collapsible = false,
  backTo = '/doc',
  backLabel = 'Knowledge',
}: DocumentTableOfContentsProps) {
  if (headings.length === 0) return null

  const content = (
    <>
      <div className="aw-doc-toc-top">
        <ListTree className="h-4 w-4" aria-hidden />
        <span>Outline</span>
      </div>

      <nav className="aw-doc-toc-list" aria-label="Headings">
        {headings.map((heading, index) =>
          useHashLinks ? (
            <a
              key={heading.id}
              href={`#${heading.id}`}
              className={cn('aw-doc-toc-link', `level-${heading.level}`)}
            >
              {heading.text}
            </a>
          ) : (
            <button
              key={heading.id}
              type="button"
              className={cn('aw-doc-toc-link', `level-${heading.level}`)}
              onClick={() => scrollToHeading(index, headingSelector)}
            >
              {heading.text}
            </button>
          ),
        )}
      </nav>

      <Link to={backTo} className="aw-doc-toc-back">
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
        {backLabel}
      </Link>
    </>
  )

  if (collapsible) {
    return (
      <details className={cn('aw-doc-toc aw-doc-toc-collapsible', className)}>
        <summary>On this page</summary>
        {content}
      </details>
    )
  }

  return (
    <aside className={cn('aw-doc-toc', className)} aria-label="Document outline">
      {content}
    </aside>
  )
}
